import { List, Text } from '@mantine/core';
import { IconHistory, IconPlayerSkipForward, IconSparkles, IconTool } from '@tabler/icons-react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { OperationError, OperationLog } from './ExecutedOperationView';
import ExecutedOperationView from './ExecutedOperationView';
import type { ExecutedOperationPair } from './ExecutionStepViewer';
import type { AiActionLog, AiChange, conditionalBlockResult } from '~/types';
import ExecutionStepTextLogsDisplay from './ExecutionStepTextLogsDisplay';
import AiActionLogDisplay from './AiActionLogDisplay';

/**
 * Displays a summary of AI changes/fixes attempted for a step.
 * Shown right after the errored operation for better visibility.
 */
function AiChangesSummary({ aiChanges, stepHealSuccess }: { aiChanges: AiChange[]; stepHealSuccess?: boolean }) {
  return (
    <div
      className={clsx(
        'mt-2 rounded-md text-xs',
        stepHealSuccess ? 'bg-gray-100 dark:bg-neutral-700' : 'bg-red-50 dark:bg-red-900/20',
      )}
    >
      <div
        className={clsx(
          'mb-2 flex flex-row gap-x-2 rounded-t-md px-1 py-1',
          stepHealSuccess ?
            'bg-blue-500/20 text-blue-600 dark:bg-blue-600/20 dark:text-blue-400'
          : 'bg-red-400/20 text-red-600 dark:bg-red-600/20 dark:text-red-400',
        )}
      >
        <IconSparkles size={14} />
        AI {stepHealSuccess ? 'successfully fixed' : 'attempted to fix'} {aiChanges.length} issue
        {aiChanges.length === 1 ? '' : 's'}
      </div>
      <div className="flex flex-row gap-x-2 px-2 pb-3">
        <List
          spacing="md"
          size="sm"
          icon={<IconTool size={14} />}
        >
          {aiChanges.map((change, i) => (
            <List.Item key={i}>{change.summary}</List.Item>
          ))}
        </List>
      </div>
    </div>
  );
}

/**
 * Represents an operation that errored and triggered AI autoheal.
 * Used to display the original failed operation separately before showing healed operations.
 */
export type TraceErroredOperation = {
  operation: SelectedStep['operations'][number];
  step: SelectedStep;
  operationError?: OperationError;
  operationLogs: OperationLog[];
  aiChanges: AiChange[];
  errorScreenshotUrl?: string; // Live screenshot captured when operation failed and was eligible for auto-heal
};

export default function ExecutionOperationDisplayList({
  operationPairs,
  inputStep,
  outputStep,
  operationErrorByOperationId,
  operationLogsByOperationId,
  selectOperationsForScreenshot,
  selectedOperations,
  aiChanges,
  conditionalBlockResultsForStep,
  stepHealSuccess,
  traceErroredOperations,
  traceErroredOperationIds,
  aiActionLogs = [],
  wasExecuted = true,
}: {
  operationPairs: ExecutedOperationPair[];
  inputStep?: SelectedStep;
  outputStep?: SelectedStep;
  operationErrorByOperationId?: Partial<Record<string, OperationError>>;
  operationLogsByOperationId?: Partial<Record<string, OperationLog[]>>;
  selectOperationsForScreenshot?: (params: {
    originalOperationId?: string;
    newOperationId?: string;
    rightDirectScreenshotUrl?: string;
    rightDirectScreenshots?: { before?: string; action?: string; after?: string };
  }) => void;
  selectedOperations?: { originalOperationId?: string; newOperationId?: string };
  aiChanges?: AiChange[];
  conditionalBlockResultsForStep?: conditionalBlockResult[];
  stepHealSuccess?: boolean;
  traceErroredOperations?: TraceErroredOperation[];
  traceErroredOperationIds?: string[];
  aiActionLogs?: AiActionLog[];
  wasExecuted?: boolean;
}) {
  const [selectedAiActionLogId, setSelectedAiActionLogId] = useState<string | undefined>();

  const handleOperationScreenshotClick = (pair: ExecutedOperationPair) => {
    // Clear AI action log selection when clicking on an operation
    setSelectedAiActionLogId(undefined);

    if (!selectOperationsForScreenshot) {
      return;
    }
    selectOperationsForScreenshot({
      originalOperationId: pair.inputOperation?.id,
      newOperationId: pair.outputOperation?.id,
    });
  };

  const traceErroredOperationIdSet = useMemo(() => new Set(traceErroredOperationIds ?? []), [traceErroredOperationIds]);

  // Build a map from operation ID to TraceErroredOperation for quick lookup
  const traceErroredOperationMap = useMemo(() => {
    if (!traceErroredOperations || traceErroredOperations.length === 0) {
      return new Map();
    }
    const map = new Map<string, TraceErroredOperation>();
    traceErroredOperations.forEach((traceOp) => {
      map.set(traceOp.operation.id, traceOp);
    });
    return map;
  }, [traceErroredOperations]);

  const getOperationError = (operationId: string | undefined): OperationError | undefined => {
    if (!operationId || traceErroredOperationIdSet.has(operationId)) {
      return undefined;
    }
    return operationErrorByOperationId?.[operationId];
  };

  if (operationPairs.length === 0) {
    return null;
  }

  const autohealStartIndex = operationPairs.findIndex((pair) => pair.isAutoheal);

  const rows: ReactNode[] = [];
  let hasRenderedErrorHeader = false;

  operationPairs.forEach((pair, idx) => {
    // Check if this operation pair has a corresponding errored operation
    // We check both input (pre-heal) and output (post-heal) operations as a fallback
    // because the error metadata may reference either depending on when it was recorded
    const traceErrorForThisPair =
      (pair.inputOperation && traceErroredOperationMap.get(pair.inputOperation.id)) ||
      (pair.outputOperation && traceErroredOperationMap.get(pair.outputOperation.id));

    // Skip rendering the normal output view when the trace error corresponds to either:
    // 1. The output operation directly (error was recorded against output)
    // 2. The input operation at the same index (error was recorded against input, output is the healed version)
    // This prevents duplicated error display since the errored operation is already rendered above.
    const shouldSkipOutputOperation =
      traceErrorForThisPair != null &&
      pair.outputOperation != null &&
      (traceErrorForThisPair.operation.id === pair.outputOperation.id ||
        traceErrorForThisPair.operation.id === pair.inputOperation?.id);

    // If there's an errored operation for this pair, render it first
    if (traceErrorForThisPair) {
      // Only highlight the first error with red styling to draw attention to the root failure;
      // subsequent retry attempts use neutral styling to reduce visual clutter
      const isOriginalErroredOperation = !hasRenderedErrorHeader;

      rows.push(
        <div
          key={`trace-${traceErrorForThisPair.operation.id}-${idx}`}
          className="table-row"
        >
          <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] px-4 py-1 align-top" />
          <div className="table-cell w-1/2 px-4 py-1 align-top">
            <div
              className={clsx(
                'rounded-lg p-2',
                isOriginalErroredOperation ?
                  'border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                : 'bg-neutral-100 dark:bg-neutral-800',
              )}
            >
              {isOriginalErroredOperation ?
                <div className="mb-1 flex items-center gap-2">
                  <Text
                    size="sm"
                    className="font-medium text-red-700 dark:text-red-300"
                  >
                    Errored action
                  </Text>
                </div>
              : null}
              <ExecutedOperationView
                operation={traceErrorForThisPair.operation}
                step={traceErrorForThisPair.step}
                operationError={traceErrorForThisPair.operationError}
                operationLogs={traceErrorForThisPair.operationLogs}
                aiChanges={traceErrorForThisPair.aiChanges}
                onScreenshotClick={() =>
                  selectOperationsForScreenshot?.({
                    originalOperationId: undefined,
                    newOperationId: traceErrorForThisPair.operation.id,
                    rightDirectScreenshotUrl: traceErrorForThisPair.errorScreenshotUrl,
                  })
                }
                selectedOperationId={selectedOperations?.newOperationId}
                showError={true}
                errorScreenshotUrl={traceErrorForThisPair.errorScreenshotUrl}
                isErroredOperation={true}
              />
            </div>
          </div>
        </div>,
      );
      hasRenderedErrorHeader = true;
    }

    rows.push(
      <div
        key={`pair-${idx}`}
        className="table-row"
      >
        <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] px-4 py-1 align-top">
          {(() => {
            // Display original test operation; prefer inputOperation, fall back to outputOperation for older runs without input content
            const originalOperation = pair.inputOperation ?? pair.outputOperation;
            const originalStep = inputStep ?? outputStep;
            if (!originalOperation || !originalStep) {
              return null;
            }

            if (pair.isAutoheal && pair.inputOperation && inputStep) {
              return (
                <div className="rounded-lg bg-neutral-100 p-2 dark:bg-neutral-900">
                  {autohealStartIndex !== -1 && idx === autohealStartIndex && (
                    <div className="mb-1 flex items-center gap-2">
                      <IconHistory
                        size={16}
                        className="text-neutral-600 dark:text-neutral-400"
                      />
                      <Text
                        size="sm"
                        className="font-medium text-neutral-700 dark:text-neutral-300"
                      >
                        Original actions
                      </Text>
                    </div>
                  )}
                  <div className="pt-2 opacity-70">
                    <ExecutedOperationView
                      operation={pair.inputOperation}
                      step={inputStep}
                      operationError={getOperationError(pair.inputOperation.id)}
                      operationLogs={operationLogsByOperationId?.[pair.inputOperation.id] ?? []}
                      onScreenshotClick={() => handleOperationScreenshotClick(pair)}
                      selectedOperationId={selectedOperations?.originalOperationId}
                      showError={true}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div className="pt-2">
                <ExecutedOperationView
                  operation={originalOperation}
                  step={originalStep}
                  operationError={getOperationError(originalOperation.id)}
                  operationLogs={operationLogsByOperationId?.[originalOperation.id] ?? []}
                  onScreenshotClick={() => handleOperationScreenshotClick(pair)}
                  selectedOperationId={selectedOperations?.originalOperationId}
                  showError={true}
                />
              </div>
            );
          })()}
        </div>

        <div className="table-cell w-1/2 px-4 py-1 align-top">
          {shouldSkipOutputOperation ?
            null
          : pair.isAutoheal && pair.outputOperation && outputStep ?
            <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/20">
              {autohealStartIndex !== -1 && idx === autohealStartIndex && (
                <div className="mb-1 flex items-center gap-2">
                  <IconSparkles
                    size={16}
                    className="text-purple-600 dark:text-purple-400"
                  />
                  <Text
                    size="sm"
                    className="font-medium text-purple-700 dark:text-purple-300"
                  >
                    AI-healed actions
                  </Text>
                </div>
              )}
              <div className="pt-2">
                <ExecutedOperationView
                  operation={pair.outputOperation}
                  step={outputStep}
                  operationError={getOperationError(pair.outputOperation.id)}
                  operationLogs={operationLogsByOperationId?.[pair.outputOperation.id] ?? []}
                  onScreenshotClick={() => handleOperationScreenshotClick(pair)}
                  selectedOperationId={selectedOperations?.newOperationId}
                  showError={true}
                  aiActionLogDisplay={
                    pair.outputOperation.type === 'AI_ACTION' && aiActionLogs.length > 0 ?
                      <AiActionLogDisplay
                        logs={aiActionLogs.filter((log) => log.parentOperationId === pair.outputOperation!.id)}
                        selectOperationsForScreenshot={selectOperationsForScreenshot}
                        selectedLogId={selectedAiActionLogId}
                        onLogSelect={setSelectedAiActionLogId}
                      />
                    : undefined
                  }
                />
              </div>
            </div>
          : pair.outputOperation && outputStep ?
            (() => {
              const hasError = !!getOperationError(pair.outputOperation.id);
              const content = (
                <ExecutedOperationView
                  operation={pair.outputOperation}
                  step={outputStep}
                  operationError={getOperationError(pair.outputOperation.id)}
                  operationLogs={operationLogsByOperationId?.[pair.outputOperation.id] ?? []}
                  onScreenshotClick={() => handleOperationScreenshotClick(pair)}
                  selectedOperationId={selectedOperations?.newOperationId}
                  showError={true}
                  aiActionLogDisplay={
                    pair.outputOperation.type === 'AI_ACTION' && aiActionLogs.length > 0 ?
                      <AiActionLogDisplay
                        logs={aiActionLogs.filter((log) => log.parentOperationId === pair.outputOperation!.id)}
                        selectOperationsForScreenshot={selectOperationsForScreenshot}
                        selectedLogId={selectedAiActionLogId}
                        onLogSelect={setSelectedAiActionLogId}
                      />
                    : undefined
                  }
                />
              );
              // Wrap in a card when there's an error
              return hasError ?
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-2 dark:border-neutral-700 dark:bg-neutral-900">
                    {content}
                  </div>
                : <div className="pt-2">{content}</div>;
            })()
          : null}
        </div>
      </div>,
    );
  });

  // If step wasn't executed, show a "Not executed" indicator
  if (!wasExecuted) {
    // Display original test operations; fall back to outputStep for older runs without input content
    const originalStep = inputStep ?? outputStep;

    return (
      <div className="table w-full py-1">
        <div className="table-row">
          {/* Input operations on the left */}
          <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] px-4 py-2 align-top">
            {operationPairs.map((pair, idx) => {
              const originalOperation = pair.inputOperation ?? pair.outputOperation;
              if (!originalOperation || !originalStep) {
                return null;
              }
              return (
                <div
                  key={`input-${idx}`}
                  className="py-1"
                >
                  <ExecutedOperationView
                    operation={originalOperation}
                    step={originalStep}
                    operationError={getOperationError(originalOperation.id)}
                    operationLogs={operationLogsByOperationId?.[originalOperation.id] ?? []}
                    onScreenshotClick={() => handleOperationScreenshotClick(pair)}
                    selectedOperationId={selectedOperations?.originalOperationId}
                    showError={true}
                  />
                </div>
              );
            })}
          </div>
          {/* "Not executed" indicator on the right */}
          <div className="table-cell w-1/2 px-4 py-4 align-top">
            <div className="flex items-center justify-center rounded-lg bg-neutral-100 p-3 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
              <IconPlayerSkipForward
                size={16}
                className="flex-shrink-0"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="table w-full py-1">
      {rows}

      {/* Step-level AI changes summary and conditional block results */}
      <div className="table-row">
        {/* Empty left column for alignment */}
        <div className="table-cell w-1/2 border-r border-[var(--mantine-color-default-border)] align-top" />
        {/* AI changes summary and conditional results in right column */}
        <div className="table-cell w-1/2 px-4 align-top">
          {aiChanges != null && aiChanges.length > 0 && (
            <AiChangesSummary
              aiChanges={aiChanges}
              stepHealSuccess={stepHealSuccess}
            />
          )}
          <ExecutionStepTextLogsDisplay conditionalBlockResultsForStep={conditionalBlockResultsForStep ?? []} />
        </div>
      </div>
    </div>
  );
}
